import { useMemo } from 'react';
import { ModuleShell } from '../components/ModuleShell';
import { DataTable, type Column } from '../components/DataTable';
import { Dot, Mono, StatusChip } from '../components/StatusChip';
import { timeAgo } from '../api/format';
import type { DomainHealthResponse, DomainWorkspace, SystemHealthResponse, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';

interface Props { moduleState: ModuleState<DomainHealthResponse> & { refetch: () => void }; filters: Filters; systemHealth: ModuleState<SystemHealthResponse> & { refetch: () => void } }

export function DomainHealthModule({ moduleState, filters, systemHealth }: Props) {
  const raw = moduleState.data?.workspaces || [];
  const webhooks = systemHealth.data?.webhook_health || [];

  const workspaces = useMemo(() => {
    let r = [...raw];
    if (filters.client) r = r.filter(w => w.client_id === filters.client);
    if (filters.search) { const q = filters.search.toLowerCase(); r = r.filter(w => w.client_name.toLowerCase().includes(q) || w.name.toLowerCase().includes(q)); }
    return r;
  }, [raw, filters]);

  function wsWebhookColor(wsId: string): string {
    const w = webhooks.find(x => x.workspace_id === wsId);
    if (!w) return 'var(--text-ghost)';
    if (w.webhook_status === 'healthy') return 'var(--green)';
    if (w.webhook_status === 'warning') return 'var(--yellow)';
    return 'var(--text-ghost)';
  }

  const columns: Column<DomainWorkspace>[] = [
    { key: 'client', label: 'Client', width: '150px', sortValue: r => r.client_name, render: r => <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{r.client_name}</span> },
    { key: 'workspace', label: 'Workspace', width: '150px', render: r => <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.name}</span> },
    { key: 'domains', label: 'Domains', align: 'right', sortValue: r => r.total_domains, render: r => <Mono color="var(--text-muted)">{r.total_domains ?? '—'}</Mono> },
    { key: 'accounts', label: 'Accounts', align: 'right', sortValue: r => r.total_accounts, render: r => <Mono color="var(--text-muted)">{r.total_accounts ?? '—'}</Mono> },
    { key: 'synced', label: 'Last Synced', align: 'right', sortValue: r => r.last_synced_at || '', render: r => <span style={{ fontSize: 11, color: r.last_synced_at ? 'var(--text-secondary)' : 'var(--text-dim)' }}>{r.last_synced_at ? timeAgo(r.last_synced_at) : 'never'}</span> },
    { key: 'wh', label: 'WH', align: 'right', width: '60px', render: r => {
      const w = webhooks.find(x => x.workspace_id === r.id);
      if (!w) return <Mono color="var(--text-dim)">—</Mono>;
      return <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5 }}>
        <Dot color={wsWebhookColor(r.id)} size={6} />
        <span style={{ fontSize: 10, color: wsWebhookColor(r.id), textTransform: 'uppercase', fontWeight: 600 }}>{w.webhook_status.replace('_', ' ')}</span>
      </div>;
    }},
  ];

  const neverSynced = workspaces.filter(w => !w.last_synced_at).length;

  return (
    <ModuleShell name="domain-health" title="Domain Health" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={neverSynced > 0 ? <StatusChip label={`${neverSynced} never synced`} variant="warning" /> : <StatusChip label="All synced" variant="success" />}>

      {/* Honest about what this module can and can't show */}
      <div style={{ padding: '8px 16px', marginBottom: 12, background: 'var(--purple-bg)', border: '1px solid var(--purple-border)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--purple-bright)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <StatusChip label="~ limited" variant="proxy" showIcon />
        Backend currently returns workspace metadata only. Inbox/spam rates, warmup status, and domain risk scores are not yet available from this endpoint.
      </div>
      {neverSynced > 0 && (
        <div style={{ padding: '8px 16px', marginBottom: 12, background: 'var(--yellow-bg)', border: '1px solid var(--yellow-border)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--yellow)' }}>
          ⚠ {neverSynced} workspace{neverSynced > 1 ? 's have' : ' has'} never synced — warmup data not available for these.
        </div>
      )}

      <DataTable columns={columns} rows={workspaces} rowKey={r => r.id} emptyMessage="No domain health data" />
    </ModuleShell>
  );
}
